import * as React from 'react';
import { ExternalLink, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  fetchPortfolio, 
  convertHoldingsToInvestments,
  isAuthenticated,
  getAuthState
} from '@/utils/kiteConnect';
import KiteConnectAuth from '@/components/auth/KiteConnectAuth';
import ZerodhaDebug from './ZerodhaDebug';

// Zerodha logo
const ZERODHA_LOGO = "https://zerodha.com/static/images/logo.svg";

interface BrokerConnectionProps {
  onConnectionComplete?: (data: any) => void;
}

const BrokerConnection: React.FC<BrokerConnectionProps> = ({ onConnectionComplete }) => {
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [connected, setConnected] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [portfolioData, setPortfolioData] = React.useState<any>(null);
  
  // Check for existing connection on component mount
  React.useEffect(() => {
    const authState = getAuthState();
    if (authState.isAuthenticated) {
      setConnected(true);
      // Fetch portfolio data if authenticated
      handleFetchPortfolio();
    }
  }, []);

  const handleFetchPortfolio = async () => {
    try {
      setIsConnecting(true);
      setError(null);
      
      // Fetch portfolio data from Kite
      const portfolioResult = await fetchPortfolio();
      
      if (portfolioResult.success) {
        setPortfolioData(portfolioResult);
        
        // Convert Kite holdings to app's investment format
        const investments = convertHoldingsToInvestments(portfolioResult.holdings);
        
        // Notify parent component
        if (onConnectionComplete) {
          onConnectionComplete({
            broker: 'zerodha',
            connected: true,
            investments: investments,
            portfolioData: portfolioResult
          });
        }
      } else {
        setError(portfolioResult.error || 'Failed to fetch portfolio data');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to fetch portfolio data');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleAuthSuccess = (userData: any) => {
    setConnected(true);
    setError(null);
    
    // Fetch portfolio data after successful authentication
    handleFetchPortfolio();
  };

  const handleAuthError = (error: string) => {
    setError(error);
    setConnected(false);
  };

  const handleAuthClear = () => {
    setConnected(false);
    setPortfolioData(null);
    setError(null);
    
    // Notify parent component
    if (onConnectionComplete) {
      onConnectionComplete({
        broker: 'zerodha',
        connected: false,
        investments: [],
        portfolioData: null
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <img 
              src={ZERODHA_LOGO} 
              alt="Zerodha" 
              className="h-8 w-8"
            />
            <div>
              <CardTitle>Connect Your Zerodha Account</CardTitle>
              <CardDescription>
                Import your existing portfolio from Zerodha Kite
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <KiteConnectAuth
            onAuthSuccess={handleAuthSuccess}
            onAuthError={handleAuthError}
            onAuthClear={handleAuthClear}
          />
        </CardContent>
      </Card>

      {/* Portfolio Data Summary */}
      {connected && portfolioData && (
        <Card>
          <CardHeader>
            <CardTitle>Portfolio Summary</CardTitle>
            <CardDescription>
              Data imported from your Zerodha account
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900">Holdings</h4>
                <p className="text-2xl font-bold text-blue-800">
                  {portfolioData.holdings?.length || 0}
                </p>
                <p className="text-sm text-blue-700">
                  Long-term investments
                </p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-medium text-green-900">Positions</h4>
                <p className="text-2xl font-bold text-green-800">
                  {portfolioData.positions?.net?.length || 0}
                </p>
                <p className="text-sm text-green-700">
                  Active positions
                </p>
              </div>
            </div>

            {portfolioData.holdings && portfolioData.holdings.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium mb-2">Sample Holdings</h4>
                <div className="space-y-2">
                  {portfolioData.holdings.slice(0, 3).map((holding: any, index: number) => (
                    <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <span className="font-medium">{holding.tradingsymbol}</span>
                      <span className="text-sm text-gray-600">
                        {holding.quantity} shares @ ₹{holding.last_price}
                      </span>
                    </div>
                  ))}
                  {portfolioData.holdings.length > 3 && (
                    <p className="text-sm text-gray-600">
                      ...and {portfolioData.holdings.length - 3} more
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="mt-4 flex gap-3">
              <Button 
                onClick={handleFetchPortfolio}
                disabled={isConnecting}
                variant="outline"
                className="flex-1"
              >
                {isConnecting ? 'Refreshing...' : 'Refresh Portfolio'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alternative: Manual Upload */}
      <Card>
        <CardHeader>
          <CardTitle>Alternative: Manual Upload</CardTitle>
          <CardDescription>
            If you prefer not to connect your broker account, you can manually upload your portfolio
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className="space-y-4">
            <div className="bg-yellow-50 p-4 rounded-lg">
              <h4 className="font-medium text-yellow-900 mb-2">
                Export from Zerodha Console
              </h4>
              <ol className="text-sm text-yellow-700 space-y-1">
                <li>1. Login to your Zerodha Console</li>
                <li>2. Go to Portfolio → Holdings</li>
                <li>3. Click "Export" to download CSV</li>
                <li>4. Upload the file below</li>
              </ol>
            </div>
            
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => window.open('https://console.zerodha.com/portfolio/holdings', '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Zerodha Console
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Debug Component */}
      {import.meta.env.DEV && (
        <ZerodhaDebug />
      )}
    </div>
  );
};

export default BrokerConnection;